/**
 * @fileoverview Zod Schemas für Laufzeit-Konfigurationsvalidierung.
 *
 * Dieses Modul definiert typsichere Schemas für die Validierung der Anwendungskonfiguration,
 * die zur Laufzeit aus config.json geladen wird. Stellt sicher dass alle benötigte Konfiguration vorhanden
 * und valide ist bevor die Anwendung startet.
 *
 * Hauptmerkmale:
 * - Zod Schemas für Laufzeit-Typprüfung
 * - TypeScript Type Inference aus Schemas
 * - Umfassende Validierung für alle Config-Bereiche
 * - URL-Validierung für Endpunkte
 * - Bereichsvalidierung für numerische Werte
 */

import { z } from 'zod';

/**
 * Logging-Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const LoggingConfigSchema = z.object({
  level: z.number().min(0).max(5),
});

/**
 * Authentifizierungs-Konfigurationsschema (OpenID Connect/Keycloak).
 *
 * @constant
 * @type {ZodObject}
 */
export const AuthConfigSchema = z.object({
  authority: z.string().url(),
  clientId: z.string().min(1),
  scope: z.string().min(1),
  logLevel: z.number().min(0).max(5),
  renewTimeBeforeTokenExpiresInSeconds: z.number().positive(),
});

/**
 * Chatbot API Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const ChatbotConfigSchema = z.object({
  /**
   * Backend-Base-URL. Erlaubt drei Formen:
   * - Absolute URL (`https://api-wart.adnova.de/chatbot-assistant`) für
   *   Direkt-Zugriff (Backend muss CORS erlauben).
   * - Leerer String (`""`) oder relativer Pfad (`"/api"`) für
   *   Dev-Proxy-Modus - Requests gehen an same-origin und der Angular
   *   Dev-Server proxied sie an das Backend (siehe proxy.conf.js).
   */
  apiBaseUrl: z
    .string()
    .refine(
      (val) => val === '' || val.startsWith('/') || /^https?:\/\//.test(val),
      { message: 'apiBaseUrl muss leer, relativer Pfad oder absolute HTTP(S)-URL sein' },
    ),
  /**
   * API-Version für SSE-Endpunkte
   *
   * @default 'v1'
   */
  apiVersion: z.string().default('v1'),
  /**
   * SSE Stream Timeout in Millisekunden
   *
   * Maximale Dauer für SSE-Verbindungen bevor Timeout
   *
   * @default 300000 (5 Minuten)
   */
  sseStreamTimeoutMs: z.number().positive().default(300000),
  /**
   * Standard HTTP Request Timeout in Millisekunden
   *
   * @default 30000 (30 Sekunden)
   */
  defaultTimeoutMs: z.number().positive().default(30000),
  /**
   * Steuert, ob Nachrichten per Server-Sent-Events (true) oder klassisch
   * per POST /messages (false) gesendet werden. Fallback auf `true`.
   *
   * @default true
   */
  streamingEnabled: z.boolean().default(true),
  /**
   * Auth-Modus fuer Chatbot-Requests.
   * - 'bearer': reiner OIDC-Bearer-Token (Wart, Produktion).
   * - 'x-user-id': zusaetzlich X-User-ID Header aus JWT.sub. Workaround fuer
   *   Dev-Backends, die keinen Bearer validieren.
   *
   * @default 'bearer'
   */
  authMode: z.enum(['bearer', 'x-user-id']).default('bearer'),
});

/**
 * Portal-Integrations-Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const PortalConfigSchema = z.object({
  targetOrigin: z.string().min(1),
});

/**
 * Sentry Error Tracking Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const SentryConfigSchema = z.object({
  enabled: z.boolean(),
  debug: z.boolean(),
  dsn: z.string().optional(),
  sampleRate: z.number().min(0).max(1),
});

/**
 * Matomo Analytics Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const MatomoConfigSchema = z.object({
  enabled: z.boolean(),
  trackerUrl: z.string().optional(),
  siteId: z.union([z.string(), z.number()]).optional(),
});

/**
 * Chat Feature Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const ChatConfigSchema = z.object({
  /**
   * Standard-Tags für Query Filters
   *
   * Flexible Tag-Konfiguration für RAG-Suche und Antwort-Filterung.
   * Wird automatisch bei jeder Chat-Nachricht mitgesendet.
   *
   * Werte können ein einzelnes Tag (string) oder mehrere Tags (string[]) sein –
   * z. B. mehrere Produkte gleichzeitig. Entspricht `DocumentFiltersInput.tags`.
   *
   * @example { "category": "Just Farming" }
   * @example { "product": "ADNOVA+", "version": "2.0" }
   * @example { "product": ["ADNOVA+", "ADNOVA Finance"] }
   * @default {}
   */
  defaultTags: z.record(z.union([z.string(), z.array(z.string())])).optional().default({}),
  /**
   * Vorschlagsfragen auf dem Willkommensbildschirm – ausschließlich pro Deployment
   * über die Umgebungsvariable CHAT_SUGGESTIONS_JSON konfigurierbar.
   * Ohne Konfiguration: keine Vorschläge (leeres Array). Keine hartkodierten Fragen.
   */
  suggestions: z.array(z.string()).optional().default([]),
  /**
   * Retry-Verzögerung bei Fehler in Millisekunden
   *
   * @default 180000 (3 Minuten)
   */
  retryDelayMs: z.number().positive().default(180000),
  /**
   * Nachrichten-Konfiguration
   */
  message: z
    .object({
      /**
       * Maximale Länge einer Chat-Nachricht
       *
       * @default 4000
       */
      maxLength: z.number().positive().default(4000),
    })
    // Optional wie in IConfig - die Consumer haben eigene Fallbacks
    .optional(),
  /**
   * Conversation-Konfiguration
   */
  conversation: z
    .object({
      /**
       * Maximale Länge eines Conversation-Titels
       *
       * @default 100
       */
      titleMaxLength: z.number().positive().default(100),
    })
    // Optional wie in IConfig - die Consumer haben eigene Fallbacks
    .optional(),
  /**
   * System Prompt Override Konfiguration (Prototyp-Feature)
   *
   * Ermoeglicht das Ueberschreiben des System-Prompts pro Request.
   * Default: deaktiviert (sicherheitskritisch).
   */
  systemPromptOverride: z
    .object({
      /** Feature-Gate: Muss explizit aktiviert werden */
      enabled: z.boolean().default(false),
      /** Standard Prompt-Version wenn keine angegeben */
      defaultPromptVersion: z.enum(['assistant_stateful', 'assistant_stateless']).default('assistant_stateful'),
    })
    .optional(),
  /**
   * Feedback-Feature-Gate.
   * Wenn true, rendern Assistenten-Antworten die Feedback-Buttons
   * (Gut/Teilweise/Schlecht) und oeffnen den Feedback-Dialog.
   *
   * @default true
   */
  feedback: z
    .object({
      enabled: z.boolean().default(true),
    })
    .optional(),
  /**
   * Debug-Panel-Feature-Gate.
   * Wenn true, zeigt der 3-Punkte-Button an Assistenten-Nachrichten ein
   * Panel mit Prompt-Version, Antwortzeit und Message-ID. Zusaetzlich wird
   * das "Custom Prompt"-Badge sichtbar.
   *
   * @default false
   */
  debugPanel: z
    .object({
      enabled: z.boolean().default(false),
    })
    .optional(),
  /**
   * Feature-Gate fuer die Quellenangaben (Citations) unter Assistenten-Antworten.
   * Wenn true, wird die "Quellen"-Liste mit den Quell-Auszuegen angezeigt.
   *
   * @default true
   */
  citations: z
    .object({
      enabled: z.boolean().default(true),
    })
    .optional(),
  /**
   * Feature-Gate fuer Dokument-Upload durch den User.
   * UI-Feature noch nicht implementiert - Flag dient als Vorbereitung,
   * damit das Backend-Endpoint bei Bedarf per Env ein-/ausschaltbar ist.
   *
   * @default false
   */
  documentsUpload: z
    .object({
      enabled: z.boolean().default(false),
    })
    .optional(),
  /**
   * Feature-Gate fuer die Tag-Vergabe beim Dokument-Upload (Chips + Vorschlaege).
   * Greift nur, wenn auch der Dokument-Upload aktiv ist.
   *
   * @default false
   */
  tags: z
    .object({
      enabled: z.boolean().default(false),
    })
    .optional(),

  /**
   * Retriever-Override je Chat-Request (RAG-Gründlichkeit).
   * 0 = kein Override (Backend-Default gilt). topK: Anzahl Chunks im Prompt
   * (1-100); scoreThreshold: minimaler Score (0-1); candidateCount: Anzahl
   * Kandidaten fürs Reranking aus dem Vector Store (1-200, seit API 0.23.0).
   */
  retriever: z
    .object({
      topK: z.number().int().min(0).max(100).default(0),
      scoreThreshold: z.number().min(0).max(1).default(0),
      candidateCount: z.number().int().min(0).max(200).default(0),
    })
    .optional(),
});

/**
 * Resilience & Fehlertoleranz Konfigurationsschema.
 *
 * @constant
 * @type {ZodObject}
 */
export const ResilienceConfigSchema = z.object({
  /**
   * Circuit Breaker Konfiguration
   */
  circuitBreaker: z.object({
    /**
     * Anzahl der Fehler bevor Circuit öffnet
     *
     * @default 5
     */
    failureThreshold: z.number().positive().default(5),
    /**
     * Anzahl erfolgreicher Requests für Circuit-Schließung
     *
     * @default 2
     */
    successThreshold: z.number().positive().default(2),
    /**
     * Timeout in Millisekunden bevor Circuit von OPEN zu HALF_OPEN wechselt
     *
     * @default 60000 (1 Minute)
     */
    timeoutMs: z.number().positive().default(60000),
  }),
  /**
   * Retry Strategy Konfiguration
   */
  retry: z.object({
    /**
     * Maximale Anzahl von Wiederholungsversuchen
     *
     * @default 3
     */
    maxRetries: z.number().nonnegative().default(3),
    /**
     * Initiale Verzögerung in Millisekunden
     *
     * @default 1000 (1 Sekunde)
     */
    initialDelayMs: z.number().positive().default(1000),
    /**
     * Maximale Verzögerung in Millisekunden
     *
     * @default 60000 (1 Minute)
     */
    maxDelayMs: z.number().positive().default(60000),
    /**
     * Backoff-Multiplikator für exponentielle Verzögerung
     *
     * @default 2
     */
    backoffMultiplier: z.number().positive().default(2),
  }),
});

/**
 * Hauptanwendungs-Konfigurationsschema.
 *
 * Kombiniert alle Sub-Schemas in eine vollständige Konfigurationsstruktur.
 *
 * @constant
 * @type {ZodObject}
 */
export const ConfigSchema = z.object({
  environment: z.string().min(1),
  logging: LoggingConfigSchema,
  auth: AuthConfigSchema,
  chatbot: ChatbotConfigSchema,
  chat: ChatConfigSchema.optional(),
  resilience: ResilienceConfigSchema.optional(),
  portal: PortalConfigSchema,
  sentry: SentryConfigSchema,
  matomo: MatomoConfigSchema,
});

/**
 * TypeScript Type Inference aus Zod Schemas.
 *
 * Diese Typen werden automatisch aus den Schemas generiert und bieten
 * Compile-Time Typsicherheit für Konfigurationszugriff.
 */

/** Vollständiger Anwendungskonfigurationstyp */
export type IConfig = z.infer<typeof ConfigSchema>;
